package com.milta.core.network

object Endpoints {
    private const val VERSION = "v1"
    private const val API = "api/$VERSION"

    /** GET, feature-flag style config shared by mobile and the web dashboard (RoutaR kill switch). */
    const val appConfig = "$API/app-config"

    const val profile = "$API/profile"
    const val profileLanguage = "$API/profile/language"
    const val profilePhoneRequest = "$API/profile/phone/request"
    const val profilePhoneConfirm = "$API/profile/phone/confirm"
    const val profileDeleteRequest = "$API/profile/delete/request"
    const val profileDeleteConfirm = "$API/profile/delete/confirm"
    /** MIL-49 — GET, Driver-only, returns the account's one-time linking code (idempotent). */
    const val profileLinkCode = "$API/profile/link-code"
    /** GET — the Enterprises the Driver is attached to, `[{enterpriseId, name, covers}]` sorted by name;
     * reachable with an expired subscription, empty when not attached. */
    const val profileEnterprises = "$API/profile/enterprises"

    /** GET, `?query=` — address suggestions for the manual delivery entry screens. */
    const val addressAutocomplete = "$API/addresses/autocomplete"
    const val addressValidate = "$API/addresses/validate"

    /**
     * GET (Manager team dashboard, MIL-38 — returns `TeamRoutesOverviewDto`, optional `?date=`) /
     * POST (create a route, DOM-02) on the same path `api/v1/routes`.
     */
    const val routes = "$API/routes"
    const val routeMe = "$API/routes/me"
    const val routeImport = "$API/routes/import"
    /** MIL-52 — GET, paginated route history for the web dashboard's "Historique des tournées"
     * (Screen A), optional `?from=&to=&driverId=&page=&pageSize=`. */
    const val routeHistory = "$API/routes/history"
    /** GET (route detail, Driver or Enterprise — MIL-38) / other verbs below on `api/v1/routes/{id}`. */
    fun route(id: String) = "$API/routes/$id"
    fun routeStart(id: String) = "$API/routes/$id/start"
    fun routeDeliveries(routeId: String) = "$API/routes/$routeId/deliveries"
    /** MIL-55 — DELETE a single stop from an in-flight route (écran 07b). */
    fun routeDelivery(routeId: String, deliveryId: String) = "$API/routes/$routeId/deliveries/$deliveryId"
    /** MIL-99 — PUT, the route's creator corrects an unlocated stop's address before sending/start. */
    fun routeDeliveryAddress(routeId: String, deliveryId: String) = "$API/routes/$routeId/deliveries/$deliveryId/address"
    /** MIL-28 — manual entry, open to Driver + Manager, distinct from [routeDeliveries] (Enterprise-only bulk add). */
    fun routeManualDelivery(routeId: String) = "$API/routes/$routeId/deliveries/manual"
    fun routeOptimize(routeId: String) = "$API/routes/$routeId/optimize"
    /** MIL-45 — manually re-runs geocoding, e.g. after correcting an address. */
    fun routeRegeocode(routeId: String) = "$API/routes/$routeId/regeocode"
    /** POST /api/v1/routes/{routeId}/reoptimize — adds a manual stop AND re-orders the whole route in one call (MIL-35). */
    fun routeReoptimize(routeId: String) = "$API/routes/$routeId/reoptimize"
    /** POST /api/v1/routes/{routeId}/reoptimize-in-place — re-orders the existing stops, no new stop added (MIL-34, "Ré-optimiser"). */
    fun routeReoptimizeInPlace(routeId: String) = "$API/routes/$routeId/reoptimize-in-place"
    fun routeDispatch(routeId: String) = "$API/routes/$routeId/dispatch"
    /** MIL-36 — POST /routes/{id}/complete, driver-initiated end of route. */
    fun routeComplete(routeId: String) = "$API/routes/$routeId/complete"
    /** MIL-41 — POST proof of delivery (photo + status + comment) for a single stop. */
    fun routeDeliveryValidate(routeId: String, deliveryId: String) = "$API/routes/$routeId/deliveries/$deliveryId/validate"
    /** MIL-52 — GET, paginated proofs of one route for the web dashboard's "Preuves de la tournée"
     * (Screen B), optional `?page=&pageSize=`. */
    fun routeProofs(routeId: String) = "$API/routes/$routeId/proofs"

    /** POST (create a RouteBatch, MIL-39) on `api/v1/route-batches`. */
    const val routeBatches = "$API/route-batches"
    fun routeBatchReoptimize(batchId: String) = "$API/route-batches/$batchId/reoptimize"
    fun routeBatchDispatch(batchId: String) = "$API/route-batches/$batchId/dispatch"
    /** DELETE — abandons a batch not sent yet (retour 26/10, demande 2 R8). */
    fun routeBatch(batchId: String) = "$API/route-batches/$batchId"

    /** MIL-99 — GET (list, last used first) / POST (add) the Enterprise's saved loading addresses. */
    const val loadingAddresses = "$API/loading-addresses"
    /** MIL-99 — DELETE one saved loading address. */
    fun loadingAddress(id: String) = "$API/loading-addresses/$id"

    const val authRegisterOAuth = "$API/auth/register/oauth"
    const val authOAuthAuthenticate = "$API/auth/oauth/authenticate"
    const val authOAuthUpdatePhone = "$API/auth/oauth/update-phone"
    const val authOAuthAppleExchange = "$API/auth/oauth/apple/exchange"
    const val authOAuthGoogleExchange = "$API/auth/oauth/google/exchange"
    const val authRefresh = "$API/auth/refresh"
    const val authLogout = "$API/auth/logout"
    const val authLogoutAll = "$API/auth/logout-all"
    const val authOtpRequest = "$API/auth/otp/send"
    const val authOtpVerify = "$API/auth/otp/verify"
    const val authCheck = "$API/auth/check"

    // Backward-compat alias
    const val authOtpSend = authOtpRequest

    const val myTeam = "$API/enterprises/my-team"
    /** MIL-53 — GET (profil en lecture seule) / DELETE (suppression définitive) sur le même chemin. */
    const val myEnterprise = "$API/enterprises/me"
    fun enterpriseDrivers(enterpriseId: String) = "$API/enterprises/$enterpriseId/drivers"
    /** MIL-49 — POST body `{code}`, links a driver to the enterprise via their one-time code. */
    fun teamLink(enterpriseId: String) = "$API/enterprises/$enterpriseId/team/link"
    /** MIL-49 — DELETE, breaks the Membership only (the driver's Milta account stays untouched). */
    fun teamMember(enterpriseId: String, driverId: String) = "$API/enterprises/$enterpriseId/team/$driverId"

    const val subscriptionMe      = "$API/subscriptions/me"
    const val subscriptionReceipt = "$API/subscriptions/receipt"
    const val subscriptionPlans   = "$API/subscriptions/plans"

    /** POST /api/v1/imports/pdf — single synchronous Claude Haiku extraction of a PDF delivery note, no session (MIL-26). */
    const val importsPdf = "$API/imports/pdf"

    /** POST /api/v1/ocr-scans — Claude Vision scan of a photographed paper delivery list (DOM-02, no session). */
    const val ocrScans = "$API/ocr-scans"

    /** POST /api/v1/spreadsheet-imports — server-side AI column mapping of an uploaded Excel/CSV file (MIL-27, no session). */
    const val spreadsheetImports = "$API/spreadsheet-imports"

    /** Asynchronous AI imports. GET `?active=true` lists the account's running / unacknowledged jobs. */
    const val importJobs = "$API/import-jobs"
    /**
     * POST multipart `file` (+ `scanAttemptId?`, OCR only) → 202. [flowSegment] is `pdf`,
     * `spreadsheet` or `ocr-delivery-list`: the flow is in the route to keep per-flow rate limits.
     */
    fun importJobSubmit(flowSegment: String) = "$API/import-jobs/$flowSegment"
    /** GET one import job — 404 when it does not belong to the account. */
    fun importJob(id: String) = "$API/import-jobs/$id"
    /** POST → 202 — relaunches a failed job from the stored file, no upload. 404 if [flowSegment] is not the job's flow. */
    fun importJobRetry(flowSegment: String, id: String) = "$API/import-jobs/$flowSegment/$id/retry"
    /** POST → 204 — abandons a Pending, Processing or Failed job. */
    fun importJobCancel(id: String) = "$API/import-jobs/$id/cancel"
    /** POST → 204 — the app consumed the result. */
    fun importJobAcknowledge(id: String) = "$API/import-jobs/$id/acknowledge"

    /** GET /api/v1/legal-documents/{type}?lang= — public, no auth (backend repo). [type] is the
     * URL slug, e.g. [com.milta.feature.legal.domain.entity.urlSlug]. */
    fun legalDocument(type: String) = "$API/legal-documents/$type"

    /** GET /api/v1/admin/accounts — Role.Admin only, cross-tenant, read-only (Dashboard Web
     * "Utilisateurs"). */
    const val adminAccounts = "$API/admin/accounts"

    /** GET, the 5 live Claude prompts (Dashboard Web "Prompts"). */
    const val adminPrompts = "$API/admin/prompts"
    /** PUT — overrides a prompt's editable body only. */
    fun adminPrompt(key: String) = "$API/admin/prompts/$key"
    /** POST — deletes the override, reverts to the hardcoded default body. */
    fun adminPromptReset(key: String) = "$API/admin/prompts/$key/reset"

    /** GET — background OCR analysis pipeline cost-comparison view (Dashboard Web "Usage IA"). */
    const val adminOcrAnalysis = "$API/admin/ocr-analysis"

    /** MIL-97 — GET, the caller's own RoutaR optimization counter (remaining / limit / reset date). */
    const val optimizationQuotaMe = "$API/optimization-quota/me"
    /** MIL-97 — GET, `?page=&pageSize=`, the caller's optimization history, most recent first. */
    const val optimizationQuotaMeHistory = "$API/optimization-quota/me/history"
    /** MIL-97 — GET / PUT, the 4 global optimization limits (Admin "Limites d'usage"). */
    const val adminOptimizationLimits = "$API/admin/usage-limits/optimization"
    /** MIL-97 — GET, one account's counter (Admin "Fiche utilisateur"). */
    fun adminAccountOptimizationQuota(accountId: String) = "$API/admin/accounts/$accountId/optimization-quota"
    /** MIL-97 — GET, `?page=&pageSize=`, one account's optimization history. */
    fun adminAccountOptimizationHistory(accountId: String) = "$API/admin/accounts/$accountId/optimization-quota/history"
    /** MIL-97 — PUT `{remaining}`, replaces the account's remaining count for the current period. */
    fun adminAccountOptimizationRemaining(accountId: String) = "$API/admin/accounts/$accountId/optimization-quota/remaining"

    /** MIL-98 — GET, the caller's own AI credit, as a percentage only (never euros nor tokens). */
    const val aiCreditMe = "$API/ai-credit/me"
    /** MIL-98 — GET, `?page=&pageSize=`, the caller's AI credit history, most recent first. */
    const val aiCreditMeHistory = "$API/ai-credit/me/history"
    /** MIL-98 — GET / PUT, the 4 AI credit limits in euros, the euro/dollar rate and the low-credit threshold. */
    const val adminAiCreditSettings = "$API/admin/usage-limits/ai"
    /** MIL-98 — GET, one account's AI credit in euros (Admin "Fiche utilisateur"). */
    fun adminAccountAiCredit(accountId: String) = "$API/admin/accounts/$accountId/ai-credit"
    /** MIL-98 — GET, `?page=&pageSize=`, one account's AI calls with tokens and cost. */
    fun adminAccountAiCreditHistory(accountId: String) = "$API/admin/accounts/$accountId/ai-credit/history"
    /** MIL-98 — PUT `{remainingEur}`, replaces the account's remaining credit for the current period. */
    fun adminAccountAiCreditRemaining(accountId: String) = "$API/admin/accounts/$accountId/ai-credit/remaining"

    /** GET, `?period=` — real per-account/per-organisation Claude token consumption (Dashboard Web
     * "Conso. IA"), distinct from [adminOcrAnalysis]'s background cost-comparison experiment. */
    const val adminClaudeUsage = "$API/admin/claude-usage"
    /** GET, `?period=&page=&pageSize=` — paginated per-call drill-down for one account. */
    fun adminClaudeUsageEvents(accountId: String) = "$API/admin/claude-usage/accounts/$accountId/events"
}
